import React, { useRef, useEffect, useLayoutEffect, useCallback, useState, memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Copy, User, Bot } from 'lucide-react';
import MessageWithArtifacts from './MessageWithArtifacts';
import { SignalBars } from './SignalBars';
import { cn } from '../../utils/cn';

// Line count threshold for collapsing long user messages
const LONG_MESSAGE_LINES = 8;
const COLLAPSED_HEIGHT = '10rem'; // ~8 lines of text

/**
 * Memoized message component to prevent unnecessary re-renders
 */
const ChatMessage = memo(function ChatMessage({
  message,
  isLastMessage,
  loading,
  progressMessage,
  toolCallsInProgress,
  onCopy
}) {
  const isUser = message.role === 'user';
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  // Check if content overflows the collapsed height
  useEffect(() => {
    if (isUser && contentRef.current) {
      const lineHeight = parseFloat(getComputedStyle(contentRef.current).lineHeight) || 24;
      const lines = contentRef.current.scrollHeight / lineHeight;
      setIsOverflowing(lines > LONG_MESSAGE_LINES);
    }
  }, [isUser, message.text]);

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-4xl">
        {isUser ? (
          // User message - compact, with hanging avatar icon on right
          <div className="flex justify-end items-start gap-3">
            <div className="max-w-[85%] px-4 py-2.5 rounded-2xl bg-stone-200/70 text-stone-900 print:bg-gray-100">
              <div className="relative">
                <div
                  ref={contentRef}
                  className="whitespace-pre-wrap text-[15px] leading-relaxed font-medium overflow-hidden"
                  style={{
                    maxHeight: isOverflowing && !isExpanded ? COLLAPSED_HEIGHT : undefined,
                  }}
                >
                  {message.text}
                </div>
                {/* Gradient fade when collapsed */}
                {isOverflowing && !isExpanded && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to bottom, transparent, rgb(214 211 209 / 0.7))'
                    }}
                  />
                )}
              </div>
              {isOverflowing && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-1 text-xs text-stone-500 hover:text-stone-700 transition-colors"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
            {/* Hanging avatar icon - aligned right */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center -mr-11 mt-1">
              <User size={16} className="text-stone-600" />
            </div>
          </div>
        ) : (
          // Assistant message - full-width, with hanging bot icon
          <div className="flex items-start gap-3">
            {/* Hanging bot icon with signal bars */}
            <div className="flex-shrink-0 -ml-11 flex items-center relative" style={{ marginTop: '0.125rem' }}>
              {/* Signal bars appear above when streaming */}
              <SignalBars isActive={loading && isLastMessage && !message.done} />

              <Bot size={24} className="text-violet-400" />
            </div>
            <div
              className="relative group flex-1 min-w-0 overflow-hidden"
              role={isLastMessage && loading ? 'log' : undefined}
              aria-live={isLastMessage && loading ? 'polite' : undefined}
            >
              {/* Progress history - subtle accordion at top */}
              {message.done && message.progressMessages && message.progressMessages.length > 0 && (
                <details className="mb-4 text-xs text-zinc-400" style={{ marginTop: '0.125rem' }}>
                  <summary className="cursor-pointer hover:text-zinc-600 transition-colors">
                    {message.progressMessages.length} operations performed
                  </summary>
                  <div className="mt-2 space-y-1 pl-3 border-l border-zinc-200">
                    {message.progressMessages.map((prog, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-zinc-500">
                        <span className="text-emerald-500 text-[10px]">✓</span>
                        <span>{prog}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Current progress message with diagonal shimmer effect */}
              {loading && isLastMessage && !message.done && progressMessage && (
                <div className="mb-3 text-sm font-medium relative overflow-hidden" style={{ marginTop: '0.125rem' }}>
                  <span className="relative text-violet-400">{progressMessage}</span>
                  <div
                    className="absolute top-0 bottom-0 w-[30%] bg-gradient-to-r from-transparent via-white to-transparent opacity-80"
                    style={{
                      animation: 'shimmer 2s infinite linear',
                      left: '-30%'
                    }}
                  />
                </div>
              )}

              <MessageWithArtifacts
                content={message.text}
              />
              {/* Streaming caret */}
              {!message.done && loading && isLastMessage && message.text && (
                <span className="inline-block w-[2px] h-[1em] bg-zinc-400 animate-pulse ml-0.5 align-text-bottom" />
              )}

              {/* Copy button - appears on hover */}
              {message.done && (
                <button
                  onClick={() => onCopy(message.text)}
                  className={cn(
                    "absolute -top-1 -right-10 p-1.5 h-auto opacity-0 group-hover:opacity-100",
                    "transition-opacity text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100",
                    "rounded-md print:hidden"
                  )}
                  aria-label="Copy message"
                >
                  <Copy size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  const prevProgKey = (prevProps.message.progressMessages || []).join('|');
  const nextProgKey = (nextProps.message.progressMessages || []).join('|');

  return (
    prevProps.message.text === nextProps.message.text &&
    prevProps.message.done === nextProps.message.done &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProgKey === nextProgKey &&
    (prevProps.message.toolCalls || 0) === (nextProps.message.toolCalls || 0) &&
    (prevProps.isLastMessage ?
      prevProps.loading === nextProps.loading &&
      prevProps.progressMessage === nextProps.progressMessage &&
      prevProps.toolCallsInProgress === nextProps.toolCallsInProgress
      : true)
  );
});

/**
 * Virtualized chat messages component - simplified to match TSX version
 */
export default function VirtualizedChatMessages({
  messages,
  loading,
  progressMessage,
  toolCallsInProgress = 0,
  onCopy,
  scrollRef,
  bottomPadding = 256,
  onFollowChange,
  onScrollToBottom,
  onScrollBy,
}) {
  const internalScrollRef = useRef(null);
  // Use external ref if provided, otherwise use internal
  const scrollElRef = scrollRef || internalScrollRef;
  const safeMessages = messages ?? [];

  // "follow" = should we auto-stick to bottom?
  const [follow, setFollow] = useState(true);
  const followRef = useRef(true);
  useEffect(() => { followRef.current = follow; }, [follow]);

  // Notify parent when follow state changes
  useEffect(() => {
    onFollowChange?.(follow);
  }, [follow, onFollowChange]);

  // Ignore scroll events caused by our own scrollToIndex calls
  const ignoreScrollRef = useRef(false);

  const virtualizer = useVirtualizer({
    count: safeMessages.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 240,
    overscan: 6,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // "At bottom" check with forgiving threshold for sticky feel
  const isAtBottom = useCallback(() => {
    const el = scrollElRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    // Use 50px threshold - forgiving enough to stay "stuck" during small variations
    return distance <= 50;
  }, []);

  // Scroll to absolute bottom - uses scrollHeight to ensure we see bottom padding
  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollElRef.current;
    if (!el) return;

    ignoreScrollRef.current = true;

    // Scroll to absolute max height - this ensures bottom padding is visible
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });

    // Give browser time to finish scroll
    requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  }, []);

  // Scroll by a delta amount (for wheel forwarding from parent)
  const scrollBy = useCallback((deltaY) => {
    const el = scrollElRef.current;
    if (!el) return;

    ignoreScrollRef.current = true;
    el.scrollTop += deltaY;

    requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  }, []);

  // Expose scrollBy to parent
  useEffect(() => {
    onScrollBy?.(scrollBy);
  }, [onScrollBy, scrollBy]);

  // Expose scrollToBottom to parent, wrapped to also set follow state
  useEffect(() => {
    onScrollToBottom?.((smooth = false) => {
      followRef.current = true;
      setFollow(true);
      scrollToBottom(smooth);
    });
  }, [onScrollToBottom, scrollToBottom]);

  // User scroll -> update follow based on actual position
  useEffect(() => {
    const el = scrollElRef.current;
    if (!el) return;

    const onScroll = () => {
      if (ignoreScrollRef.current) return;
      const atBottom = isAtBottom();
      if (atBottom !== followRef.current) {
        setFollow(atBottom);
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initialize
    return () => el.removeEventListener('scroll', onScroll);
  }, [isAtBottom]);

  // THE KEY EFFECT: When content height changes and we're following, pin to bottom
  // This fires after virtualizer measures, so we scroll to the true bottom
  useLayoutEffect(() => {
    if (!followRef.current) return;
    scrollToBottom(false); // Instant jump during streaming to prevent jitter
  }, [totalSize, scrollToBottom]);

  // When a new message is added, force follow=true for user messages
  // (this ensures we scroll to bottom when user submits a query mid-chat)
  const prevLenRef = useRef(0);
  useEffect(() => {
    const prevLen = prevLenRef.current;
    const nextLen = safeMessages.length;
    prevLenRef.current = nextLen;
    if (nextLen <= prevLen) return;

    const lastMsg = safeMessages[nextLen - 1];
    // Force follow for user messages OR new assistant stubs (empty assistant = just started)
    const isUserMsg = lastMsg?.role === 'user';
    const isNewAssistantStub = lastMsg?.role === 'assistant' && !lastMsg.text && !lastMsg.done;

    if (isUserMsg || isNewAssistantStub) {
      followRef.current = true;
      setFollow(true);
      // Don't scroll here - the totalSize effect handles it after measurement
    }
  }, [safeMessages]);

  return (
    <div
      ref={scrollElRef}
      className="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 bg-white print-expand"
      style={{
        overflowAnchor: 'none',
        paddingBottom: bottomPadding,
      }}
      role="log"
      aria-label="Chat messages"
    >
      {/* Add shimmer keyframe animation */}
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(400%); }
          }
        `}
      </style>

      <div
        style={{
          height: `${totalSize}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((vi) => {
          const message = safeMessages[vi.index];
          if (!message) return null;

          return (
            <div
              key={message.id || `msg-${vi.index}`}
              ref={virtualizer.measureElement}
              data-index={vi.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vi.start}px)`,
              }}
              className="py-3"
            >
              <ChatMessage
                message={message}
                isLastMessage={vi.index === safeMessages.length - 1}
                loading={loading}
                progressMessage={progressMessage}
                toolCallsInProgress={toolCallsInProgress}
                onCopy={onCopy}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
